import { useEffect, useMemo, useRef, useState, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import { type GeodataTarget } from 'contexts/GeodataContext';
import { type SubSimulation, type SimulationState } from 'contexts/SimulationContext';
import { detectWaterFeaturesInArea } from 'services/targetLakeDetection';
import { getTargetRadiusMax } from 'constants/geodata';
import {
  buildStandardTargets,
  getTargetViewDefinitions,
  getTargetsSignature,
  resolveTargetDetectionArea,
  type SimulationGeodata,
  type SimulationGridView,
} from './addSimulationShared';

type UseSimulationTargetsSyncArgs = {
  osmDetectionRequestVersion: number;
  shouldDetectOsmTargets: boolean;
  geodata: SimulationGeodata | null;
  geodataParams: Record<string, unknown>;
  grid: SimulationGridView | null;
  isCloneSessionRef: MutableRefObject<boolean>;
  isReadOnlyReview: boolean;
  setGeodata: Dispatch<SetStateAction<SimulationGeodata | null>>;
  setGeodataParams: (params: Record<string, unknown>) => void;
  setSimulation: Dispatch<SetStateAction<SimulationState>>;
  simulation: SimulationState;
};

const useSimulationTargetsSync = ({
  osmDetectionRequestVersion,
  shouldDetectOsmTargets,
  geodata,
  geodataParams,
  grid,
  isCloneSessionRef,
  isReadOnlyReview,
  setGeodata,
  setGeodataParams,
  setSimulation,
  simulation,
}: UseSimulationTargetsSyncArgs) => {
  const lakeRadius = getTargetRadiusMax(Number(geodataParams.lengthMid), Number(geodataParams.lengthFar));
  const [customTargets, currentTargets] = useMemo(() => {
    const targets = Array.isArray(geodataParams.targets) ? (geodataParams.targets as GeodataTarget[]) : [];
    return [targets.filter((target) => target?.type === 'CUSTOM'), targets] as const;
  }, [geodataParams.targets]);
  const [osmState, setOsmState] = useState({
    hasLakeInArea: false,
    hasRiverInArea: false,
    hasDetectionFailed: false,
    isLoadingOsmTargets: shouldDetectOsmTargets && !isReadOnlyReview,
  });
  const lastCompletedOsmDetectionKeyRef = useRef<string | null>(null);
  const inFlightOsmDetectionKeyRef = useRef<string | null>(null);
  const osmRequestIdRef = useRef(0);

  useEffect(() => {
    if (isReadOnlyReview || !shouldDetectOsmTargets) {
      setOsmState((prev) => ({ ...prev, isLoadingOsmTargets: false }));
      return;
    }

    if (!grid || !geodata) {
      setOsmState((prev) => ({ ...prev, isLoadingOsmTargets: true }));
      return;
    }

    const detectionArea = resolveTargetDetectionArea(
      grid as { params?: Record<string, unknown> | null },
      geodataParams.lengthFar,
      geodataParams.lengthMid
    );
    if (!detectionArea) {
      setOsmState({
        hasLakeInArea: false,
        hasRiverInArea: false,
        hasDetectionFailed: false,
        isLoadingOsmTargets: false,
      });
      return;
    }

    const detectionKey = JSON.stringify({
      center: detectionArea.center,
      radiusMeters: Math.round(detectionArea.radiusMeters),
      requestVersion: osmDetectionRequestVersion,
    });

    if (lastCompletedOsmDetectionKeyRef.current === detectionKey) {
      setOsmState((prev) => ({ ...prev, isLoadingOsmTargets: false }));
      return;
    }

    if (inFlightOsmDetectionKeyRef.current === detectionKey) {
      setOsmState((prev) => ({ ...prev, isLoadingOsmTargets: true }));
      return;
    }

    const requestId = ++osmRequestIdRef.current;
    inFlightOsmDetectionKeyRef.current = detectionKey;
    setOsmState((prev) => ({ ...prev, hasDetectionFailed: false, isLoadingOsmTargets: true }));

    detectWaterFeaturesInArea(detectionArea)
      .then(({ hasLake, hasRiver }) => {
        if (requestId !== osmRequestIdRef.current) {
          return;
        }

        lastCompletedOsmDetectionKeyRef.current = detectionKey;
        setOsmState({
          hasLakeInArea: hasLake,
          hasRiverInArea: hasRiver,
          hasDetectionFailed: false,
          isLoadingOsmTargets: true,
        });
      })
      .catch((error) => {
        if (requestId !== osmRequestIdRef.current) {
          return;
        }

        console.warn('Unable to detect OSM water targets while opening simulations', error);
        lastCompletedOsmDetectionKeyRef.current = detectionKey;
        setOsmState({
          hasLakeInArea: false,
          hasRiverInArea: false,
          hasDetectionFailed: true,
          isLoadingOsmTargets: true,
        });
      })
      .finally(() => {
        if (requestId === osmRequestIdRef.current) {
          inFlightOsmDetectionKeyRef.current = null;
          setOsmState((prev) => ({ ...prev, isLoadingOsmTargets: false }));
        }
      });
  }, [
    geodata,
    geodataParams.lengthFar,
    geodataParams.lengthMid,
    grid,
    isReadOnlyReview,
    osmDetectionRequestVersion,
    shouldDetectOsmTargets,
  ]);

  useEffect(() => {
    if (!shouldDetectOsmTargets || isReadOnlyReview || !grid || !geodata) {
      return;
    }

    const nextTargets = buildStandardTargets(
      { hasLake: osmState.hasLakeInArea, hasRiver: osmState.hasRiverInArea },
      customTargets,
      geodataParams.minArea,
      lakeRadius
    );

    if (getTargetsSignature(nextTargets) !== getTargetsSignature(currentTargets)) {
      setGeodataParams({ targets: nextTargets });
    }
  }, [
    currentTargets,
    customTargets,
    geodata,
    geodataParams.minArea,
    grid,
    isReadOnlyReview,
    lakeRadius,
    osmState.hasLakeInArea,
    osmState.hasRiverInArea,
    setGeodataParams,
    shouldDetectOsmTargets,
  ]);

  useEffect(() => {
    if (!shouldDetectOsmTargets) return;

    setGeodata((previousGeodata) => {
      if (!previousGeodata) {
        return previousGeodata;
      }

      const previousTargets = Array.isArray(previousGeodata.params?.targets) ? previousGeodata.params.targets : [];
      const nextTargets = currentTargets;
      const hasSameTargets =
        previousTargets.length === nextTargets.length &&
        previousTargets.every((target, index) => target === nextTargets[index]);

      if (hasSameTargets) {
        return previousGeodata;
      }

      return {
        ...previousGeodata,
        params: {
          ...(previousGeodata.params ?? {}),
          targets: nextTargets,
        },
      };
    });
  }, [currentTargets, setGeodata, shouldDetectOsmTargets]);

  useEffect(() => {
    if (!shouldDetectOsmTargets || isReadOnlyReview) return;

    const geodataTargets =
      currentTargets.length > 0
        ? currentTargets
        : Array.isArray(geodata?.params?.targets)
          ? (geodata.params.targets as GeodataTarget[])
          : null;
    if (!geodataTargets) {
      return;
    }

    const targetViewDefinitions = getTargetViewDefinitions(lakeRadius);

    setSimulation((prevSimulation) => {
      const previousSubSimulations = Array.isArray(prevSimulation?.subSimulations) ? prevSimulation.subSimulations : [];
      const nonTargetViewSubSimulations = previousSubSimulations.filter((subSim) => subSim?.simType !== 'TARGETVIEW');
      const previousTargetViewSubSimulations = previousSubSimulations.filter(
        (subSim) => subSim?.simType === 'TARGETVIEW'
      );
      const shouldMaintainTargetViewSubSimulations =
        prevSimulation?.useStandardSettings ||
        prevSimulation?.targetViewEnabled ||
        previousTargetViewSubSimulations.length > 0;
      const shouldPreserveCloneWithoutTargetView =
        isCloneSessionRef.current &&
        !prevSimulation?.targetViewEnabled &&
        previousTargetViewSubSimulations.length === 0;

      if (shouldPreserveCloneWithoutTargetView || !shouldMaintainTargetViewSubSimulations) {
        return prevSimulation;
      }

      const resolveTargetIds = (subSim: SubSimulation): number[] =>
        (Array.isArray(subSim?.params?.targetIds) ? subSim.params.targetIds : [])
          .map((value: unknown) => Number(value))
          .filter((value: number) => Number.isFinite(value));

      const geodataTargetsById = new Map<number, GeodataTarget>();
      geodataTargets.forEach((target) => {
        const targetId = Number(target?.id);
        if (Number.isFinite(targetId)) {
          geodataTargetsById.set(targetId, target);
        }
      });

      const resolveTargetsForSubSimulation = (subSim: SubSimulation): GeodataTarget[] => {
        const rawParamTargets = Array.isArray(subSim?.params?.targets) ? subSim.params.targets : [];
        const paramTargets = rawParamTargets.filter(
          (target): target is GeodataTarget => Boolean(target) && typeof target === 'object' && !Array.isArray(target)
        );
        const targetIdsFromParams = rawParamTargets
          .map((target: unknown) => {
            if (typeof target === 'number' || typeof target === 'string') {
              const value = Number(target);
              return Number.isFinite(value) ? value : null;
            }

            const targetId = Number((target as { id?: unknown })?.id);
            return Number.isFinite(targetId) ? targetId : null;
          })
          .filter((targetId): targetId is number => Number.isFinite(targetId));

        if (paramTargets.length > 0) {
          return paramTargets;
        }

        const targetIds = resolveTargetIds(subSim);
        const resolvedTargetIds = targetIds.length > 0 ? targetIds : targetIdsFromParams;
        if (resolvedTargetIds.length === 0) {
          return [];
        }

        return resolvedTargetIds
          .map((targetId) => geodataTargetsById.get(targetId))
          .filter((target): target is GeodataTarget => Boolean(target));
      };

      const hasResolvedTargets = (subSim: SubSimulation) => {
        const targetIds = resolveTargetIds(subSim);
        if (targetIds.length > 0) {
          return true;
        }

        return resolveTargetsForSubSimulation(subSim).some((target) => Number.isFinite(Number(target?.id)));
      };

      let nextSubSimulationId =
        previousSubSimulations.length > 0
          ? Math.max(...previousSubSimulations.map((subSim) => Number(subSim?.id) || 0)) + 1
          : 0;
      const matchedStandardTargetViewSubSimulationIds = new Set<number>();
      const standardTargetTypes = new Set<string>(targetViewDefinitions.map((definition) => definition.type));

      const getSubSimulationTargetType = (subSim: SubSimulation) => {
        const targets = resolveTargetsForSubSimulation(subSim);
        const targetTypes = [...new Set(targets.map((target) => target?.type).filter(Boolean))];
        return targetTypes.length === 1 ? String(targetTypes[0]) : null;
      };

      const nextTargetViewSubSimulations = targetViewDefinitions
        .map((definition) => {
          const targetsForType = geodataTargets.filter((target) => String(target?.type) === definition.type);
          const targetIds = targetsForType
            .map((target) => Number(target?.id))
            .filter((targetId) => Number.isFinite(targetId));
          const existingSubSimulation = previousTargetViewSubSimulations.find(
            (subSim) => getSubSimulationTargetType(subSim) === definition.type
          );
          const representativeTarget = targetsForType[0];
          const nextTargets =
            targetsForType.length > 0 ? targetsForType : [{ type: definition.type, name: definition.name }];

          if (existingSubSimulation) {
            matchedStandardTargetViewSubSimulationIds.add(existingSubSimulation.id);
            const hadResolvedTargets = hasResolvedTargets(existingSubSimulation);
            return {
              ...existingSubSimulation,
              name: existingSubSimulation.name || definition.name,
              params: {
                ...existingSubSimulation.params,
                enabled:
                  targetsForType.length > 0 &&
                  (!hadResolvedTargets ? definition.defaultEnabled : (existingSubSimulation.params?.enabled ?? false)),
                desiredCellCount: Number.isFinite(Number(existingSubSimulation.params?.desiredCellCount))
                  ? Number(existingSubSimulation.params.desiredCellCount)
                  : 2000,
                radius: Number.isFinite(Number(existingSubSimulation.params?.radius))
                  ? Number(existingSubSimulation.params.radius)
                  : Number(representativeTarget?.radius ?? definition.defaultRadius),
                targetIds,
                targets: nextTargets,
              },
            };
          }

          const newSubSimulation: SubSimulation = {
            id: nextSubSimulationId,
            name: definition.name,
            simType: 'TARGETVIEW',
            type: 'subsimulation',
            params: {
              enabled: targetsForType.length > 0 ? definition.defaultEnabled : false,
              desiredCellCount: 2000,
              radius: Number(representativeTarget?.radius ?? definition.defaultRadius),
              targetIds,
              targets: nextTargets,
            },
          };
          nextSubSimulationId += 1;
          return newSubSimulation;
        })
        .filter((subSimulation): subSimulation is SubSimulation => Boolean(subSimulation));

      const additionalTargetViewSubSimulations = previousTargetViewSubSimulations.filter(
        (subSim) =>
          !matchedStandardTargetViewSubSimulationIds.has(subSim.id) &&
          !standardTargetTypes.has(getSubSimulationTargetType(subSim) ?? '')
      );

      const nextSubSimulations = [
        ...nonTargetViewSubSimulations.filter((subSim) => subSim?.simType !== 'NOISE' && subSim?.simType !== 'WIND'),
        ...nextTargetViewSubSimulations,
        ...additionalTargetViewSubSimulations,
        ...nonTargetViewSubSimulations.filter((subSim) => subSim?.simType === 'NOISE' || subSim?.simType === 'WIND'),
      ];

      const hasChanged =
        nextSubSimulations.length !== previousSubSimulations.length ||
        nextSubSimulations.some((subSim, index) => subSim !== previousSubSimulations[index]);

      if (!hasChanged) {
        return prevSimulation;
      }

      return {
        ...prevSimulation,
        subSimulations: nextSubSimulations,
      };
    });
  }, [
    currentTargets,
    geodata?.params?.targets,
    isCloneSessionRef,
    isReadOnlyReview,
    lakeRadius,
    setSimulation,
    simulation?.targetViewEnabled,
    simulation?.useStandardSettings,
    shouldDetectOsmTargets,
  ]);

  return osmState;
};

export default useSimulationTargetsSync;
